import { useState, useEffect, useCallback } from "react";
import type { ReactElement, ChangeEvent } from "react";
import { API_BASE_URL } from "../config";
import { getAuthHeaders } from "../api/auth";
import "./AssortmentDistributionView.css";

interface AssortmentEntry {
    position: number; // 1-5
    stagingNumber: string;
    assortment: string;
    distribution: number;
    isCompleted: boolean;
}

interface StagingNumberOption {
    BERNR: string;
    MATNR: string | null;
    VERTRAG: string | null;
    STATUS: string | null;
}

interface MaterialOption {
    MATNR: string;
    MAKTX: string | null;
    VERTRAG: string | null;
}

interface AssortmentDistributionViewProps {
    batchTransportId: string;
    onClose: () => void;
    onSave?: () => void;
}

export function AssortmentDistributionView({
    batchTransportId,
    onClose,
    onSave
}: AssortmentDistributionViewProps): ReactElement {
    const [assortmentEntries, setAssortmentEntries] = useState<AssortmentEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    // Dropdown options
    const [spediteurNummer, setSpediteurNummer] = useState<string | null>(null);
    const [werks, setWerks] = useState<string | null>(null);
    const [stagingNumberOptions, setStagingNumberOptions] = useState<StagingNumberOption[]>([]);
    const [materialOptionsByBernr, setMaterialOptionsByBernr] = useState<Map<string, MaterialOption[]>>(new Map());
    const [loadingStagingNumbers, setLoadingStagingNumbers] = useState<boolean>(false);
    const [loadingMaterialsFor, setLoadingMaterialsFor] = useState<Set<string>>(new Set());

    // Fetch batchTransport and load assortment distribution
    const loadBatchTransport = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/batch-transports/${batchTransportId}`,
                { headers: getAuthHeaders(), credentials: "include" }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const batchTransport = await response.json();

            // Extract SPEDITEUR and WERKS from batchTransport (or linked transport)
            let lifnrS: string | null = null;
            let plant: string | null = null;

            // Try to get SPEDITEUR from linked transport
            console.log("Full BatchTransport object:", batchTransport);

            if (batchTransport.TransportNumber) {
                // Fetch linked transport to get SPEDITEUR
                try {
                    const transportResponse = await fetch(
                        `${API_BASE_URL}/api/transports?TRANSPORTNUMMER=${encodeURIComponent(batchTransport.TransportNumber)}`,
                        { headers: getAuthHeaders(), credentials: "include" }
                    );
                    console.log("Transport response status:", transportResponse.status);

                    if (transportResponse.ok) {
                        const transports = await transportResponse.json();
                        console.log("Transports data:", transports);

                        if (Array.isArray(transports) && transports.length > 0) {
                            const transport = transports[0];
                            console.log("Transport SPEDITEUR:", transport.SPEDITEUR);
                            lifnrS = transport.SPEDITEUR || null;
                            plant = transport.WERKS || batchTransport.Plant || null;
                        } else {
                            console.warn("No transports found for TransportNumber:", batchTransport.TransportNumber);
                        }
                    } else {
                        console.error("Failed to fetch transport, status:", transportResponse.status);
                    }
                } catch (err) {
                    console.error("Failed to load linked transport:", err);
                }
            } else {
                console.warn("No TransportNumber in batchTransport");
            }

            // Fallback: use Plant from batchTransport
            if (!plant && batchTransport.Plant) {
                plant = batchTransport.Plant;
            }

            console.log("Final extracted values:", { lifnrS, plant });

            setSpediteurNummer(lifnrS);
            setWerks(plant);

            // Load assortment entries from batchTransport
            const loadedEntries: AssortmentEntry[] = [];
            const stagingNumbers = [
                batchTransport.stagingNumber,
                batchTransport.stagingNumber2,
                batchTransport.stagingNumber3,
                batchTransport.stagingNumber4,
                batchTransport.stagingNumber5
            ];
            const assortments = [
                batchTransport.assortmentPos1,
                batchTransport.assortmentPos2,
                batchTransport.assortmentPos3,
                batchTransport.assortmentPos4,
                batchTransport.assortmentPos5
            ];
            const distributions = [
                batchTransport.distributionPos1,
                batchTransport.distributionPos2,
                batchTransport.distributionPos3,
                batchTransport.distributionPos4,
                batchTransport.distributionPos5
            ];
            const finishedFlags = [
                batchTransport.stagingFinishedFlagPos1,
                batchTransport.stagingFinishedFlagPos2,
                batchTransport.stagingFinishedFlagPos3,
                batchTransport.stagingFinishedFlagPos4,
                batchTransport.stagingFinishedFlagPos5
            ];

            for (let i = 0; i < 5; i++) {
                if (stagingNumbers[i] || assortments[i]) {
                    const entry = {
                        position: i + 1,
                        stagingNumber: stagingNumbers[i] ?? "",
                        assortment: assortments[i] ?? "",
                        distribution: distributions[i] ?? 0,
                        isCompleted: finishedFlags[i] === true
                    };
                    loadedEntries.push(entry);
                }
            }
            setAssortmentEntries(loadedEntries);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Fehler beim Laden der Daten";
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [batchTransportId]);

    useEffect(() => {
        void loadBatchTransport();
    }, [loadBatchTransport]);

    // Load staging numbers when spediteurNummer is available
    useEffect(() => {
        console.log("[AssortmentDistribution] Checking for spediteurNummer:", spediteurNummer);
        console.log("[AssortmentDistribution] Werks:", werks);

        if (!spediteurNummer) {
            console.warn("[AssortmentDistribution] No spediteurNummer available - cannot load staging numbers");
            setStagingNumberOptions([]);
            return;
        }

        const loadStagingNumbers = async () => {
            setLoadingStagingNumbers(true);
            try {
                const url = new URL(`${API_BASE_URL}/api/assortment-distribution/staging-numbers`);
                url.searchParams.set("lifnrS", spediteurNummer);
                if (werks) {
                    url.searchParams.set("werks", werks);
                }

                console.log("[AssortmentDistribution] Fetching staging numbers from:", url.toString());

                const response = await fetch(url.toString(), {
                    headers: getAuthHeaders(),
                    credentials: "include"
                });

                console.log("[AssortmentDistribution] Response status:", response.status);

                if (!response.ok) {
                    throw new Error(`Failed to load staging numbers: ${response.status}`);
                }

                const data = await response.json();
                console.log("[AssortmentDistribution] Received staging numbers:", data);
                console.log("[AssortmentDistribution] Count:", Array.isArray(data) ? data.length : 0);

                setStagingNumberOptions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("[AssortmentDistribution] Failed to load staging numbers:", err);
                setStagingNumberOptions([]);
            } finally {
                setLoadingStagingNumbers(false);
            }
        };

        void loadStagingNumbers();
    }, [spediteurNummer, werks]);

    // Load materials for a specific staging number
    const loadMaterialsForBernr = useCallback(async (bernr: string) => {
        if (!bernr || loadingMaterialsFor.has(bernr)) {
            return;
        }

        setLoadingMaterialsFor(prev => new Set(prev).add(bernr));

        try {
            const url = new URL(`${API_BASE_URL}/api/assortment-distribution/materials`);
            url.searchParams.set("bernr", bernr);
            if (werks) {
                url.searchParams.set("werks", werks);
            }

            const response = await fetch(url.toString(), {
                headers: getAuthHeaders(),
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error(`Failed to load materials: ${response.status}`);
            }

            const data = await response.json();
            setMaterialOptionsByBernr(prev => {
                const updated = new Map(prev);
                updated.set(bernr, Array.isArray(data) ? data : []);
                return updated;
            });
        } catch (err) {
            console.error(`Failed to load materials for ${bernr}:`, err);
            setMaterialOptionsByBernr(prev => {
                const updated = new Map(prev);
                updated.set(bernr, []);
                return updated;
            });
        } finally {
            setLoadingMaterialsFor(prev => {
                const updated = new Set(prev);
                updated.delete(bernr);
                return updated;
            });
        }
    }, [werks, loadingMaterialsFor]);

    // Load materials for existing staging numbers
    useEffect(() => {
        assortmentEntries.forEach(entry => {
            if (entry.stagingNumber && !materialOptionsByBernr.has(entry.stagingNumber)) {
                void loadMaterialsForBernr(entry.stagingNumber);
            }
        });
    }, [assortmentEntries, materialOptionsByBernr, loadMaterialsForBernr]);

    // Add new assortment entry
    const addAssortmentEntry = useCallback(() => {
        if (assortmentEntries.length >= 5) {
            return; // Maximal 5 Einträge
        }
        const nextPosition = assortmentEntries.length + 1;

        // Erste Bereitstellung: 100%, alle weiteren: 5%
        const defaultDistribution = assortmentEntries.length === 0 ? 100 : 5;

        const newEntry: AssortmentEntry = {
            position: nextPosition,
            stagingNumber: "",
            assortment: "",
            distribution: defaultDistribution,
            isCompleted: false
        };
        setAssortmentEntries(prev => [...prev, newEntry]);
        setHasChanges(true);
    }, [assortmentEntries.length]);

    // Update an assortment entry
    const updateAssortmentEntry = useCallback(
        (position: number, field: keyof Omit<AssortmentEntry, 'position'>, value: string | number | boolean) => {
            setAssortmentEntries(prev => {
                const updated = prev.map(entry =>
                    entry.position === position ? { ...entry, [field]: value } : entry
                );
                return updated;
            });
            setHasChanges(true);

            // Load materials when staging number changes
            if (field === "stagingNumber" && typeof value === "string" && value) {
                void loadMaterialsForBernr(value);
            }
        },
        [loadMaterialsForBernr]
    );

    // Delete an assortment entry
    const deleteAssortmentEntry = useCallback((position: number) => {
        setAssortmentEntries(prev => {
            const filtered = prev.filter(entry => entry.position !== position);
            // Re-nummeriere die Positionen
            return filtered.map((entry, index) => ({
                ...entry,
                position: index + 1
            }));
        });
        setHasChanges(true);
    }, []);

    // Handle save
    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        setSaveMessage(null);

        // Validierung: Alle Bereitstellungen müssen eine BereitstellungsNr haben
        const entriesWithoutStagingNumber = assortmentEntries.filter(entry => !entry.stagingNumber);
        if (entriesWithoutStagingNumber.length > 0) {
            setSaveMessage("Fehler: Alle Bereitstellungen müssen eine Bereitstellungsnummer haben.");
            setSaving(false);
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        // Validierung: BereitstellungsNr muss aus verfügbaren Optionen ausgewählt sein
        const invalidStagingNumbers = assortmentEntries.filter(entry => {
            if (!entry.stagingNumber) return false; // Leere sind ok (werden als null gespeichert)
            // Prüfe ob die eingegebene BERNR in den verfügbaren Optionen existiert
            return !stagingNumberOptions.some(opt => opt.BERNR === entry.stagingNumber);
        });

        if (invalidStagingNumbers.length > 0) {
            setSaveMessage("Fehler: Bereitstellungsnummern müssen aus der Liste ausgewählt werden.");
            setSaving(false);
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        // Validierung: Keine Bereitstellung mit 0% erlaubt
        const hasZeroDistribution = assortmentEntries.some(entry => entry.distribution === 0);
        if (hasZeroDistribution) {
            setSaveMessage("Fehler: Bereitstellungen dürfen keine 0% Verteilung haben. Mindestens 5% erforderlich.");
            setSaving(false);
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        // Validierung: Verteilung muss 100% ergeben
        const totalDistribution = assortmentEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0);

        if (assortmentEntries.length > 0 && totalDistribution !== 100) {
            setSaveMessage(`Fehler: Die Verteilung muss insgesamt 100% ergeben (aktuell: ${totalDistribution}%)`);
            setSaving(false);
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        try {
            // Prepare assortment distribution data
            const assortmentData: Record<string, string | number | boolean | null> = {
                stagingNumber: null,
                stagingNumber2: null,
                stagingNumber3: null,
                stagingNumber4: null,
                stagingNumber5: null,
                assortmentPos1: null,
                distributionPos1: null,
                assortmentPos2: null,
                distributionPos2: null,
                assortmentPos3: null,
                distributionPos3: null,
                assortmentPos4: null,
                distributionPos4: null,
                assortmentPos5: null,
                distributionPos5: null,
                stagingFinishedFlagPos1: null,
                stagingFinishedFlagPos2: null,
                stagingFinishedFlagPos3: null,
                stagingFinishedFlagPos4: null,
                stagingFinishedFlagPos5: null
            };

            assortmentEntries.forEach((entry) => {
                const pos = entry.position;

                if (pos === 1) {
                    assortmentData.stagingNumber = entry.stagingNumber || null;
                    assortmentData.assortmentPos1 = entry.assortment || null;
                    assortmentData.distributionPos1 = entry.distribution;
                    assortmentData.stagingFinishedFlagPos1 = entry.isCompleted ? true : null;
                } else if (pos === 2) {
                    assortmentData.stagingNumber2 = entry.stagingNumber || null;
                    assortmentData.assortmentPos2 = entry.assortment || null;
                    assortmentData.distributionPos2 = entry.distribution;
                    assortmentData.stagingFinishedFlagPos2 = entry.isCompleted ? true : null;
                } else if (pos === 3) {
                    assortmentData.stagingNumber3 = entry.stagingNumber || null;
                    assortmentData.assortmentPos3 = entry.assortment || null;
                    assortmentData.distributionPos3 = entry.distribution;
                    assortmentData.stagingFinishedFlagPos3 = entry.isCompleted ? true : null;
                } else if (pos === 4) {
                    assortmentData.stagingNumber4 = entry.stagingNumber || null;
                    assortmentData.assortmentPos4 = entry.assortment || null;
                    assortmentData.distributionPos4 = entry.distribution;
                    assortmentData.stagingFinishedFlagPos4 = entry.isCompleted ? true : null;
                } else if (pos === 5) {
                    assortmentData.stagingNumber5 = entry.stagingNumber || null;
                    assortmentData.assortmentPos5 = entry.assortment || null;
                    assortmentData.distributionPos5 = entry.distribution;
                    assortmentData.stagingFinishedFlagPos5 = entry.isCompleted ? true : null;
                }
            });

            const response = await fetch(
                `${API_BASE_URL}/api/batch-transports/${batchTransportId}`,
                {
                    method: "PATCH",
                    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(assortmentData)
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            setSaveMessage("✓ Sortimentsverteilung erfolgreich gespeichert");
            setHasChanges(false);

            // Rufe onSave Callback auf, um zum nächsten Schritt zu gelangen
            if (onSave) {
                setTimeout(() => {
                    onSave();
                }, 500); // Kurze Verzögerung, damit die Erfolgsmeldung sichtbar ist
            } else {
                setTimeout(() => {
                    setSaveMessage(null);
                }, 3000);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Fehler beim Speichern";
            setError(message);
            setSaveMessage(null);
        } finally {
            setSaving(false);
        }
    }, [batchTransportId, assortmentEntries, stagingNumberOptions, onSave]);

    // Calculate total distribution
    const totalDistribution = assortmentEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0);
    const isDistributionValid = assortmentEntries.length === 0 || totalDistribution === 100;

    if (loading) {
        return (
            <div className="assortment-container">
                <p>Lädt...</p>
            </div>
        );
    }

    return (
        <div className="assortment-container">
            {saveMessage && (
                <div className={`assortment-save-notification ${saveMessage.startsWith("✓") ? "success" : "error"}`}>
                    {saveMessage}
                </div>
            )}

            <div className="assortment-content">
                {error && (
                    <div className="assortment-error-message">
                        {error}
                    </div>
                )}

                <div className="assortment-table-container">
                    <div className="assortment-table-header">
                        <h3 className="assortment-table-title">Bereitstellungen</h3>
                        <div className="assortment-header-right">
                            {assortmentEntries.length > 0 && (
                                <div className={`assortment-distribution-sum ${isDistributionValid ? 'valid' : 'invalid'}`}>
                                    Gesamt: {totalDistribution}%
                                    {!isDistributionValid && ' (muss 100% ergeben)'}
                                </div>
                            )}
                            <button
                                type="button"
                                className="assortment-add-btn"
                                onClick={addAssortmentEntry}
                                disabled={assortmentEntries.length >= 5}
                            >
                                + Neue Bereitstellung
                            </button>
                        </div>
                    </div>

                    <table className="assortment-table">
                        <thead>
                            <tr>
                                <th>Bereitstellungs-Nr.</th>
                                <th>Sortiment</th>
                                <th>Verteilung</th>
                                <th>Bereitstellung fertig</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {assortmentEntries.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="assortment-empty">
                                        Keine Bereitstellungen vorhanden. Klicken Sie auf "Neue Bereitstellung" um eine hinzuzufügen.
                                    </td>
                                </tr>
                            ) : (
                                assortmentEntries.map(entry => {
                                    const materialOptions = materialOptionsByBernr.get(entry.stagingNumber) || [];
                                    const isLoadingMaterials = loadingMaterialsFor.has(entry.stagingNumber);

                                    // Filter out materials that are already selected in other entries with the same BERNR
                                    const usedMaterialsForSameBernr = assortmentEntries
                                        .filter(e => e.stagingNumber === entry.stagingNumber && e.position !== entry.position)
                                        .map(e => e.assortment)
                                        .filter(Boolean);

                                    const availableMaterialOptions = materialOptions.filter(
                                        mat => !usedMaterialsForSameBernr.includes(mat.MATNR)
                                    );

                                    return (
                                        <tr key={entry.position}>
                                            <td>
                                                <select
                                                    className="assortment-select"
                                                    value={entry.stagingNumber}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                        updateAssortmentEntry(entry.position, "stagingNumber", e.target.value)
                                                    }
                                                    disabled={loadingStagingNumbers || !spediteurNummer}
                                                >
                                                    <option value="">
                                                        {loadingStagingNumbers ? "Lädt..." : spediteurNummer ? "-- Bitte wählen --" : "-- Kein Spediteur --"}
                                                    </option>
                                                    {stagingNumberOptions.map((opt) => (
                                                        <option key={opt.BERNR} value={opt.BERNR}>
                                                            {opt.BERNR}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    className="assortment-select"
                                                    value={entry.assortment}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                        updateAssortmentEntry(entry.position, "assortment", e.target.value)
                                                    }
                                                    disabled={isLoadingMaterials || !entry.stagingNumber || availableMaterialOptions.length === 0}
                                                >
                                                    <option value="">
                                                        {isLoadingMaterials ? "Lädt..." : !entry.stagingNumber ? "-- Bereitstellung wählen --" : availableMaterialOptions.length === 0 ? "-- Keine Materialien --" : "-- Bitte wählen --"}
                                                    </option>
                                                    {availableMaterialOptions.map((mat) => (
                                                        <option key={mat.MATNR} value={mat.MATNR}>
                                                            {mat.MAKTX || mat.MATNR}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    className="assortment-select"
                                                    value={entry.distribution}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                        updateAssortmentEntry(entry.position, "distribution", parseInt(e.target.value, 10))
                                                    }
                                                >
                                                    <option value={5}>5%</option>
                                                    <option value={10}>10%</option>
                                                    <option value={15}>15%</option>
                                                    <option value={20}>20%</option>
                                                    <option value={25}>25%</option>
                                                    <option value={30}>30%</option>
                                                    <option value={35}>35%</option>
                                                    <option value={40}>40%</option>
                                                    <option value={45}>45%</option>
                                                    <option value={50}>50%</option>
                                                    <option value={55}>55%</option>
                                                    <option value={60}>60%</option>
                                                    <option value={65}>65%</option>
                                                    <option value={70}>70%</option>
                                                    <option value={75}>75%</option>
                                                    <option value={80}>80%</option>
                                                    <option value={85}>85%</option>
                                                    <option value={90}>90%</option>
                                                    <option value={95}>95%</option>
                                                    <option value={100}>100%</option>
                                                </select>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`assortment-toggle ${entry.isCompleted ? "active" : ""}`}
                                                    onClick={() =>
                                                        updateAssortmentEntry(entry.position, "isCompleted", !entry.isCompleted)
                                                    }
                                                >
                                                    {entry.isCompleted ? "JA" : "NEIN"}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="assortment-delete-btn"
                                                    onClick={() => deleteAssortmentEntry(entry.position)}
                                                    title="Löschen"
                                                >
                                                    ✕
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="assortment-actions">
                    <button
                        type="button"
                        className="assortment-save-button"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                    >
                        {saving ? "Speichert..." : "Speichern"}
                    </button>
                </div>
            </div>
        </div>
    );
}
